import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/Header';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { useToast } from '../../components/Toast';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { normalizeDecimalInput, parseDecimal } from '../../utils/decimalInput';
import { hrTabBar, hrTabBtn } from './hrStyles';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('access_token')}` });

const GRUP_COLORS = {
  SERVIS: { bg: '#e8f5e9', color: '#2e7d32' },
  MUTFAK: { bg: '#fff3e0', color: '#e65100' },
  AMORTISMAN: { bg: '#eceff1', color: '#455a64' },
};

const SECTIONS = [
  { key: 'genel', label: 'Genel' },
  { key: 'ciro', label: 'Ciro Payı & Pozisyonlar' },
  { key: 'belge', label: 'Belge Türleri' },
];

const emptyBelgeTuru = {
  kod: '',
  ad: '',
  sureli: false,
  varsayilan_gecerlilik_ay: '',
  zorunlu: true,
  aktif: true,
  sira: 100,
};

const IkAyarlarPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_HR, user.effectivePermissions);
  const isYonetim = user && (user.role === 'yonetim' || user.role === 'admin');

  const [activeSection, setActiveSection] = useState('ciro');
  const [data, setData] = useState(null);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [ciroForm, setCiroForm] = useState({ ciro_yuzde_personel: 6, servis_orani: 55, mutfak_orani: 45 });
  const [aktifLokIds, setAktifLokIds] = useState([]);
  const [editingPoz, setEditingPoz] = useState(null);
  const [editPuan, setEditPuan] = useState('');
  // Ek mesai çarpanı düzenleme alanı (genel sekme)
  const [mesaiCarpaniInput, setMesaiCarpaniInput] = useState('');
  // İşveren SGK/vergi yük oranı (%) — cari ay tahmini vergi yükü (vergi tahakkuku Faz 2)
  const [isverenYukInput, setIsverenYukInput] = useState('');

  // Belge türleri state
  const [belgeTurleri, setBelgeTurleri] = useState([]);
  const [belgeLoading, setBelgeLoading] = useState(false);
  const [editingBelgeId, setEditingBelgeId] = useState(null);
  const [editBelgeForm, setEditBelgeForm] = useState(emptyBelgeTuru);
  const [yeniBelgeForm, setYeniBelgeForm] = useState(null);  // null → formu gizle

  const fetchData = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API_URL}/api/ik/ayarlar`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/lokasyonlar`, { headers: authHeaders() }),
      ]);
      if (r1.ok) {
        const d = await r1.json();
        setData(d);
        setCiroForm({
          ciro_yuzde_personel: d.ciro_payi.ciro_yuzde_personel,
          servis_orani: d.ciro_payi.servis_orani,
          mutfak_orani: d.ciro_payi.mutfak_orani,
        });
        setAktifLokIds(d.ciro_payi.aktif_lokasyon_ids || []);
        setMesaiCarpaniInput(String(d.fazla_mesai_carpani ?? 1.0));
        setIsverenYukInput(String(d.isveren_yuk_orani_pct ?? 0));
      }
      if (r2.ok) setLokasyonlar((await r2.json()).filter(l => l.aktif !== false));
    } catch (e) { logError(e); }
  }, []);

  const fetchBelgeTurleri = useCallback(async () => {
    setBelgeLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/hr/belge-turleri?dahil_pasif=true`, { headers: authHeaders() });
      if (r.ok) setBelgeTurleri(await r.json());
      else toast.error('Belge türleri yüklenemedi');
    } catch {
      toast.error('Bağlantı hatası');
    } finally {
      setBelgeLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (activeSection === 'belge' && belgeTurleri.length === 0 && !belgeLoading) {
      fetchBelgeTurleri();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const saveCiroAyar = async () => {
    try {
      const r = await fetch(`${API_URL}/api/ik/ayarlar/ciro-payi`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ciro_yuzde_personel: parseFloat(ciroForm.ciro_yuzde_personel),
          servis_orani: parseFloat(ciroForm.servis_orani),
          mutfak_orani: parseFloat(ciroForm.mutfak_orani),
          aktif_lokasyon_ids: aktifLokIds,
        }),
      });
      if (r.ok) { toast.success('Ayarlar güncellendi'); fetchData(); }
      else toast.error('Güncellenemedi');
    } catch { toast.error('Bağlantı hatası'); }
  };

  const togglelokAktif = (lokId) => {
    setAktifLokIds(prev => prev.includes(lokId) ? prev.filter(x => x !== lokId) : [...prev, lokId]);
  };

  // Faz D: Ciro Payı modülü global aç/kapa bayrağı
  const toggleCiroPayiModulu = async (yeniDeger) => {
    try {
      const r = await fetch(`${API_URL}/api/ik/ayarlar/ciro-payi`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ciro_payi_aktif: yeniDeger }),
      });
      if (r.ok) {
        toast.success(yeniDeger ? 'Ciro Payı modülü açıldı' : 'Ciro Payı modülü kapatıldı');
        fetchData();
      } else {
        toast.error('Güncellenemedi');
      }
    } catch { toast.error('Bağlantı hatası'); }
  };

  // Kıdem tazminatı TAKİBİ aç/kapa. Kapalı (default) = çıkışta kıdem yalnızca
  // bilgi amaçlı hesaplanır/gösterilir; sisteme ödenecek kayıt olarak işlenmez.
  const toggleKidemTakip = async (yeniDeger) => {
    try {
      const r = await fetch(`${API_URL}/api/ik/ayarlar/genel`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ kidem_tazminati_takip: yeniDeger }),
      });
      if (r.ok) {
        toast.success(yeniDeger
          ? 'Kıdem tazminatı takibi açıldı'
          : 'Kıdem tazminatı takibi kapatıldı — çıkışta sadece bilgi gösterilir');
        fetchData();
      } else {
        toast.error('Güncellenemedi');
      }
    } catch { toast.error('Bağlantı hatası'); }
  };

  // Ek mesai çarpanını kaydet (fazla_mesai_tutar = saatlik × saat × çarpan)
  const saveMesaiCarpani = async () => {
    const val = parseFloat(mesaiCarpaniInput);
    if (!(val > 0)) { toast.warning("Çarpan 0'dan büyük olmalı"); return; }
    try {
      const r = await fetch(`${API_URL}/api/ik/ayarlar/genel`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ fazla_mesai_carpani: val }),
      });
      if (r.ok) { toast.success('Ek mesai çarpanı güncellendi'); fetchData(); }
      else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Güncellenemedi');
      }
    } catch { toast.error('Bağlantı hatası'); }
  };

  // İşveren yük oranını kaydet (cari ay tahmini vergi = tahmini resmi ödeme × oran)
  const saveIsverenYuk = async () => {
    const val = parseDecimal(isverenYukInput, -1);
    if (val < 0 || val > 100) { toast.warning('Oran 0-100 arasında olmalı'); return; }
    try {
      const r = await fetch(`${API_URL}/api/ik/ayarlar/genel`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ isveren_yuk_orani_pct: val }),
      });
      if (r.ok) { toast.success('İşveren yük oranı güncellendi'); fetchData(); }
      else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Güncellenemedi');
      }
    } catch { toast.error('Bağlantı hatası'); }
  };

  const savePozisyon = async (pozId, puan) => {
    try {
      const r = await fetch(`${API_URL}/api/ik/pozisyon/${pozId}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ default_puan: parseFloat(puan) }),
      });
      if (r.ok) { toast.success('Puan güncellendi'); fetchData(); }
      else toast.error('Güncellenemedi');
    } catch { toast.error('Bağlantı hatası'); }
    setEditingPoz(null); setEditPuan('');
  };

  const startEditBelge = (b) => {
    setEditingBelgeId(b.id);
    setEditBelgeForm({
      kod: b.kod,
      ad: b.ad,
      sureli: b.sureli,
      varsayilan_gecerlilik_ay: b.varsayilan_gecerlilik_ay ?? '',
      zorunlu: b.zorunlu,
      aktif: b.aktif,
      sira: b.sira,
    });
  };

  const saveEditBelge = async () => {
    if (!editingBelgeId) return;
    const body = {
      ad: editBelgeForm.ad,
      sureli: editBelgeForm.sureli,
      varsayilan_gecerlilik_ay: editBelgeForm.sureli && editBelgeForm.varsayilan_gecerlilik_ay !== ''
        ? parseInt(editBelgeForm.varsayilan_gecerlilik_ay, 10)
        : null,
      zorunlu: editBelgeForm.zorunlu,
      aktif: editBelgeForm.aktif,
      sira: parseInt(editBelgeForm.sira, 10) || 0,
    };
    try {
      const r = await fetch(`${API_URL}/api/hr/belge-turleri/${editingBelgeId}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        toast.success('Belge türü güncellendi');
        setEditingBelgeId(null);
        fetchBelgeTurleri();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Güncellenemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  const createBelgeTuru = async () => {
    if (!yeniBelgeForm?.kod.trim() || !yeniBelgeForm?.ad.trim()) {
      toast.warning('Kod ve ad zorunlu');
      return;
    }
    const body = {
      kod: yeniBelgeForm.kod.trim().toUpperCase().replace(/\s+/g, '_'),
      ad: yeniBelgeForm.ad.trim(),
      sureli: yeniBelgeForm.sureli,
      varsayilan_gecerlilik_ay: yeniBelgeForm.sureli && yeniBelgeForm.varsayilan_gecerlilik_ay !== ''
        ? parseInt(yeniBelgeForm.varsayilan_gecerlilik_ay, 10)
        : null,
      zorunlu: yeniBelgeForm.zorunlu,
      aktif: yeniBelgeForm.aktif,
      sira: parseInt(yeniBelgeForm.sira, 10) || 0,
    };
    try {
      const r = await fetch(`${API_URL}/api/hr/belge-turleri`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        toast.success('Belge türü eklendi');
        setYeniBelgeForm(null);
        fetchBelgeTurleri();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Eklenemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  if (!canManage) return <div><Header title="İK Ayarları" /><div style={{ padding: 40, textAlign: 'center' }}>Yetkiniz yok.</div></div>;
  if (!data) return <div><Header title="İK Ayarları" /><div style={{ padding: 40, textAlign: 'center' }}>Yükleniyor...</div></div>;

  const input = { padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 14, width: 100 };
  const label = { display: 'block', fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600 };
  const smallInput = { padding: '5px 8px', fontSize: 12, border: `1px solid ${colors.border}`, borderRadius: radius.sm, boxSizing: 'border-box' };

  return (
    <div>
      <Header title="İK Ayarları" />
      <div style={{ padding: 20, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Section tab bar */}
        <div style={{ ...hrTabBar, marginBottom: 16 }}>
          {SECTIONS.map(s => {
            const disabled = s.key === 'belge' && !isYonetim;
            return (
              <button
                key={s.key}
                onClick={() => !disabled && setActiveSection(s.key)}
                disabled={disabled}
                style={{
                  ...hrTabBtn(activeSection === s.key),
                  opacity: disabled ? 0.4 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
                title={disabled ? 'Sadece yönetim/admin görebilir' : ''}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {activeSection === 'genel' && data && (
          <div style={{ background: colors.surface, padding: 20, borderRadius: radius.md, boxShadow: shadows.sm, marginBottom: 16, borderLeft: `4px solid ${data.kidem_tazminati_takip ? '#2e7d32' : '#999'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>Kıdem Tazminatı Takibi</div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4, maxWidth: 580 }}>
                  Kapalıyken personel çıkışında kıdem tazminatı yalnızca <b>bilgi amaçlı hesaplanıp gösterilir</b>;
                  sisteme ödenecek (bekleyen) kayıt olarak <b>işlenmez</b> — maddi yansıma olmaz. Açıkken çıkışta
                  "Bekleyen" kıdem kaydı oluşturulur ve personel ödeme bekliyor olarak izlenir.
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: canManage ? 'pointer' : 'not-allowed' }}>
                <input
                  type="checkbox"
                  checked={!!data.kidem_tazminati_takip}
                  disabled={!canManage}
                  onChange={(e) => toggleKidemTakip(e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                <span style={{ fontWeight: 600, color: data.kidem_tazminati_takip ? '#2e7d32' : '#999' }}>
                  {data.kidem_tazminati_takip ? 'Açık' : 'Kapalı'}
                </span>
              </label>
            </div>

            {/* Ek mesai çarpanı (fazla_mesai_tutar = saatlik × saat × çarpan) */}
            <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>Ek Mesai Çarpanı</div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4, maxWidth: 580 }}>
                  Aylık puantajda fazla mesai tutarı = <b>saatlik ücret × ek mesai saati × çarpan</b>.
                  <b> 1.0</b> = saat karşılığı (zamsız), <b>1.5</b> = yasal %50 zamlı. Hızlı Puantaj ve
                  günlükten hesaplama bu değeri kullanır.
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={mesaiCarpaniInput}
                  disabled={!canManage}
                  onChange={(e) => setMesaiCarpaniInput(e.target.value)}
                  style={{ width: 90, padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 14, textAlign: 'right' }}
                />
                <button
                  onClick={saveMesaiCarpani}
                  disabled={!canManage || String(mesaiCarpaniInput) === String(data.fazla_mesai_carpani ?? 1.0)}
                  style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: 4, cursor: canManage ? 'pointer' : 'not-allowed', fontWeight: 600, opacity: (!canManage || String(mesaiCarpaniInput) === String(data.fazla_mesai_carpani ?? 1.0)) ? 0.5 : 1 }}
                >
                  Kaydet
                </button>
              </div>
            </div>

            {/* İşveren SGK/vergi yük oranı (vergi tahakkuku Faz 2 — cari ay tahmini) */}
            <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>İşveren SGK/Vergi Yük Oranı (%)</div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4, maxWidth: 580 }}>
                  Yönetim Paneli, bordrosu <b>kapanmamış</b> (cari) ay için tahmini vergi yükünü
                  <b> tahmini resmi ödeme × bu oran</b> ile hesaplar — kâr yapay yüksek görünmesin.
                  <b> 0</b> = tahmin kapalı. Kapanan ayların <b>gerçek</b> tahakkukunu muhasebe
                  Gider Girişi'nden girer (bu oran onu etkilemez).
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={isverenYukInput}
                  disabled={!canManage}
                  onChange={(e) => setIsverenYukInput(normalizeDecimalInput(e.target.value))}
                  style={{ width: 90, padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 14, textAlign: 'right' }}
                />
                <button
                  onClick={saveIsverenYuk}
                  disabled={!canManage || String(isverenYukInput) === String(data.isveren_yuk_orani_pct ?? 0)}
                  style={{ padding: '8px 16px', background: styles.success, color: 'white', border: 'none', borderRadius: 4, cursor: canManage ? 'pointer' : 'not-allowed', fontWeight: 600, opacity: (!canManage || String(isverenYukInput) === String(data.isveren_yuk_orani_pct ?? 0)) ? 0.5 : 1 }}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'ciro' && (
          <>
            {/* Faz D: Ciro Payı Modülü global aç/kapa */}
            <div style={{ background: colors.surface, padding: 20, borderRadius: radius.md, boxShadow: shadows.sm, marginBottom: 16, borderLeft: `4px solid ${data.ciro_payi_aktif ? '#2e7d32' : '#999'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: 16 }}>🔌 Ciro Payı Modülü</h3>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Kapalıyken ciro payı sekmesi, puantaj Ciro kolonu, pozisyon kodu alanı ve ciro raporu tüm ekranlardan gizlenir.
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: data.ciro_payi_aktif ? '#2e7d32' : '#999' }}>
                    {data.ciro_payi_aktif ? 'Açık' : 'Kapalı'}
                  </span>
                  <button
                    onClick={() => toggleCiroPayiModulu(!data.ciro_payi_aktif)}
                    title={data.ciro_payi_aktif ? 'Modülü kapat' : 'Modülü aç'}
                    style={{
                      width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer',
                      background: data.ciro_payi_aktif ? '#2e7d32' : '#ccc',
                      position: 'relative', transition: 'background 0.2s', padding: 0,
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 3, left: data.ciro_payi_aktif ? 27 : 3,
                      width: 22, height: 22, borderRadius: '50%', background: 'white',
                      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }} />
                  </button>
                </div>
              </div>
            </div>

            {/* Ciro Payı Ayarları — modül kapalıyken soluk/pasif (toggle erişilebilir kalır) */}
            <div style={{ background: colors.surface, padding: 20, borderRadius: radius.md, boxShadow: shadows.sm, marginBottom: 16, opacity: data.ciro_payi_aktif ? 1 : 0.5, pointerEvents: data.ciro_payi_aktif ? 'auto' : 'none' }}>
              <h3 style={{ margin: '0 0 14px 0', fontSize: 16 }}>💵 Ciro Payı Ayarları</h3>

              <div style={{ marginBottom: 16 }}>
                <div style={label}>Aktif Lokasyonlar</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {lokasyonlar.map(l => (
                    <label key={l.id} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '6px 12px', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', background: aktifLokIds.includes(l.id) ? '#e3f2fd' : 'white' }}>
                      <input type="checkbox" checked={aktifLokIds.includes(l.id)} onChange={() => togglelokAktif(l.id)} />
                      <span style={{ fontSize: 13 }}>{l.ad}</span>
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Sadece seçili lokasyonlarda Ciro Payı modülü aktiftir.</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 14 }}>
                <div>
                  <div style={label}>Ciro'nun Personele %</div>
                  <input style={input} type="number" step="0.1" value={ciroForm.ciro_yuzde_personel} onChange={e => setCiroForm({ ...ciroForm, ciro_yuzde_personel: e.target.value })} />
                  <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>Havuz = Ciro × %{ciroForm.ciro_yuzde_personel}</div>
                </div>
                <div>
                  <div style={label}>Servis Oranı %</div>
                  <input style={input} type="number" step="0.1" value={ciroForm.servis_orani} onChange={e => setCiroForm({ ...ciroForm, servis_orani: e.target.value })} />
                  <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>Havuz × %{ciroForm.servis_orani}</div>
                </div>
                <div>
                  <div style={label}>Mutfak Oranı %</div>
                  <input style={input} type="number" step="0.1" value={ciroForm.mutfak_orani} onChange={e => setCiroForm({ ...ciroForm, mutfak_orani: e.target.value })} />
                  <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>Havuz × %{ciroForm.mutfak_orani}</div>
                </div>
              </div>

              <button onClick={saveCiroAyar} style={{ padding: '10px 24px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600 }}>
                Ciro Payı Ayarlarını Kaydet
              </button>
            </div>

            {/* Pozisyon Puan Tablosu — modül kapalıyken soluk/pasif */}
            <div style={{ background: colors.surface, padding: 20, borderRadius: radius.md, boxShadow: shadows.sm, opacity: data.ciro_payi_aktif ? 1 : 0.5, pointerEvents: data.ciro_payi_aktif ? 'auto' : 'none' }}>
              <h3 style={{ margin: '0 0 14px 0', fontSize: 16 }}>🎯 Pozisyon Puan Tablosu</h3>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                Her pozisyon için varsayılan puan. Ciro payı hesaplanınca bu puanlar kullanılır, ama her ay için override edilebilir.
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Kod</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Ad</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>Grup</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Default Puan</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>Aktif</th>
                  </tr>
                </thead>
                <tbody>
                  {data.pozisyonlar.map(p => {
                    const gc = GRUP_COLORS[p.grup] || GRUP_COLORS.AMORTISMAN;
                    const editing = editingPoz === p.id;
                    return (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, color: '#888' }}>{p.kod}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>{p.ad}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: gc.bg, color: gc.color }}>{p.grup}</span>
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                          {editing ? (
                            <input
                              autoFocus type="number" step="0.5"
                              value={editPuan}
                              onChange={e => setEditPuan(e.target.value)}
                              onBlur={() => savePozisyon(p.id, editPuan)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') savePozisyon(p.id, editPuan);
                                if (e.key === 'Escape') { setEditingPoz(null); setEditPuan(''); }
                              }}
                              style={{ width: 60, padding: 3, border: '2px solid #1565c0', borderRadius: 3, fontSize: 12 }}
                            />
                          ) : (
                            <span
                              onClick={() => { setEditingPoz(p.id); setEditPuan(p.default_puan); }}
                              style={{ cursor: 'pointer', color: '#1565c0', fontWeight: 700 }}
                            >{p.default_puan}</span>
                          )}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          {p.aktif ? <span style={{ color: '#2e7d32' }}>✓</span> : <span style={{ color: '#999' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeSection === 'belge' && isYonetim && (
          <div style={{ background: colors.surface, padding: 20, borderRadius: radius.md, boxShadow: shadows.sm }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>📄 Belge Türleri</h3>
              <button
                onClick={() => setYeniBelgeForm(yeniBelgeForm ? null : { ...emptyBelgeTuru })}
                style={{ padding: '8px 14px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
              >
                {yeniBelgeForm ? 'Formu Kapat' : '+ Yeni Tür Ekle'}
              </button>
            </div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
              Personel belge takibinde kullanılan türler. Süreli belgelerde "varsayılan geçerlilik ay" sayısı,
              alındı tarihine eklenerek otomatik son geçerlilik tarihi hesaplanır.
            </div>

            {yeniBelgeForm && (
              <div style={{ border: `2px dashed ${colors.border}`, borderRadius: radius.sm, padding: 12, marginBottom: 14, background: '#fafafa' }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Yeni Belge Türü</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 80px', gap: 8, marginBottom: 8 }}>
                  <div>
                    <label style={{ fontSize: 10, color: '#666' }}>Kod (otomatik ÜST_ÇİZGİLİ)</label>
                    <input
                      style={{ ...smallInput, width: '100%' }}
                      value={yeniBelgeForm.kod}
                      onChange={e => setYeniBelgeForm({ ...yeniBelgeForm, kod: e.target.value })}
                      placeholder="EHLIYET"
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, color: '#666' }}>Ad</label>
                    <input
                      style={{ ...smallInput, width: '100%' }}
                      value={yeniBelgeForm.ad}
                      onChange={e => setYeniBelgeForm({ ...yeniBelgeForm, ad: e.target.value })}
                      placeholder="Sürücü Ehliyeti"
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, color: '#666' }}>Sıra</label>
                    <input
                      type="number"
                      style={{ ...smallInput, width: '100%' }}
                      value={yeniBelgeForm.sira}
                      onChange={e => setYeniBelgeForm({ ...yeniBelgeForm, sira: e.target.value })}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 12 }}>
                    <input type="checkbox" checked={yeniBelgeForm.sureli} onChange={e => setYeniBelgeForm({ ...yeniBelgeForm, sureli: e.target.checked })} />
                    Süreli
                  </label>
                  {yeniBelgeForm.sureli && (
                    <label style={{ fontSize: 12 }}>
                      Varsayılan geçerlilik (ay):
                      <input
                        type="number"
                        style={{ ...smallInput, width: 70, marginLeft: 6 }}
                        value={yeniBelgeForm.varsayilan_gecerlilik_ay}
                        onChange={e => setYeniBelgeForm({ ...yeniBelgeForm, varsayilan_gecerlilik_ay: e.target.value })}
                      />
                    </label>
                  )}
                  <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 12 }}>
                    <input type="checkbox" checked={yeniBelgeForm.zorunlu} onChange={e => setYeniBelgeForm({ ...yeniBelgeForm, zorunlu: e.target.checked })} />
                    Zorunlu
                  </label>
                  <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 12 }}>
                    <input type="checkbox" checked={yeniBelgeForm.aktif} onChange={e => setYeniBelgeForm({ ...yeniBelgeForm, aktif: e.target.checked })} />
                    Aktif
                  </label>
                  <button
                    onClick={createBelgeTuru}
                    style={{ padding: '6px 14px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                  >
                    Kaydet
                  </button>
                </div>
              </div>
            )}

            {belgeLoading ? (
              <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Yükleniyor...</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Kod</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Ad</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>Süreli</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Geçerlilik (ay)</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>Zorunlu</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>Aktif</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Sıra</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {belgeTurleri.map(b => {
                    const editing = editingBelgeId === b.id;
                    if (editing) {
                      return (
                        <tr key={b.id} style={{ background: '#fff9e6', borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11, color: '#888' }}>{b.kod}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <input style={{ ...smallInput, width: '100%' }} value={editBelgeForm.ad} onChange={e => setEditBelgeForm({ ...editBelgeForm, ad: e.target.value })} />
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <input type="checkbox" checked={editBelgeForm.sureli} onChange={e => setEditBelgeForm({ ...editBelgeForm, sureli: e.target.checked })} />
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                            <input
                              type="number"
                              style={{ ...smallInput, width: 60 }}
                              value={editBelgeForm.varsayilan_gecerlilik_ay}
                              onChange={e => setEditBelgeForm({ ...editBelgeForm, varsayilan_gecerlilik_ay: e.target.value })}
                              disabled={!editBelgeForm.sureli}
                            />
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <input type="checkbox" checked={editBelgeForm.zorunlu} onChange={e => setEditBelgeForm({ ...editBelgeForm, zorunlu: e.target.checked })} />
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <input type="checkbox" checked={editBelgeForm.aktif} onChange={e => setEditBelgeForm({ ...editBelgeForm, aktif: e.target.checked })} />
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                            <input type="number" style={{ ...smallInput, width: 60 }} value={editBelgeForm.sira} onChange={e => setEditBelgeForm({ ...editBelgeForm, sira: e.target.value })} />
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <button onClick={saveEditBelge} style={{ padding: '3px 8px', fontSize: 11, background: styles.success, color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer' }}>✓</button>
                            <button onClick={() => setEditingBelgeId(null)} style={{ padding: '3px 8px', fontSize: 11, background: '#aaa', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer', marginLeft: 4 }}>✕</button>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr
                        key={b.id}
                        style={{ borderBottom: '1px solid #f0f0f0', opacity: b.aktif ? 1 : 0.5 }}
                      >
                        <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 11, color: '#888' }}>{b.kod}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>{b.ad}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>{b.sureli ? '✓' : '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{b.varsayilan_gecerlilik_ay ?? '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>{b.zorunlu ? '✓' : '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>{b.aktif ? <span style={{ color: '#2e7d32' }}>✓</span> : <span style={{ color: '#999' }}>—</span>}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{b.sira}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <button onClick={() => startEditBelge(b)} style={{ padding: '3px 10px', fontSize: 11, background: 'white', border: `1px solid ${colors.border}`, borderRadius: 3, cursor: 'pointer' }}>Düzenle</button>
                        </td>
                      </tr>
                    );
                  })}
                  {belgeTurleri.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: '#888' }}>Tür yok.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IkAyarlarPage;
